import FormikValidationError from 'components/ui/FormikErrors';
import { FormikProps } from 'formik';
import moment from 'moment';
import { FC, useEffect, useState } from 'react';
import Select from 'react-select';
import { IMannualAttendanceInput } from 'redux/reducers/attendance';
import { useListTraineeQuery } from 'redux/reducers/TraineeEvaluation';
import { useListTrainingCourseQuery } from 'redux/reducers/training';

interface IAttendanceForm {
  formik: FormikProps<IMannualAttendanceInput>;
}

const AttendanceForm: FC<IAttendanceForm> = ({ formik }) => {
  const { data: trainingData } = useListTrainingCourseQuery();
  const [selectedRoutine, setSelectedRoutine] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState<string>(moment().format('YYYY-MM-DD'));

  const { data: attendanceData, isFetching } = useListTraineeQuery(selectedRoutine?.value);

  const enrolledUsers = attendanceData?.data || [];
  const selectedUsers = (formik.values.selectedUsers as number[]) || [];

  // Check if all current users are selected
  const isAllSelected = 
    enrolledUsers.length > 0 && 
    enrolledUsers.every((user: any) => selectedUsers.includes(user.user_id));

  useEffect(() => {
    formik.setFieldValue('selectedUsers', []);
  }, [selectedRoutine, selectedDate]);

  const handleUserToggle = (userId: number, checked: boolean) => {
    if (checked) {
      formik.setFieldValue('selectedUsers', [...selectedUsers, userId]);
    } else {
      formik.setFieldValue(
        'selectedUsers',
        selectedUsers.filter((user_id: number) => user_id !== userId)
      );
    }
  };

  // Select All / Deselect All Handler
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = enrolledUsers.map((user: any) => user.user_id);
      // Use Set to ensure no duplicates if some were already selected
      const newSelection = Array.from(new Set([...selectedUsers, ...allIds]));
      formik.setFieldValue('selectedUsers', newSelection);
    } else {
      // Remove only the users currently visible in the list from the selection
      const visibleIds = enrolledUsers.map((user: any) => user.user_id);
      formik.setFieldValue(
        'selectedUsers',
        selectedUsers.filter((user_id: number) => !visibleIds.includes(user_id))
      );
    }
  };

  return (
    <div>
      <div className="row">
        <div className="col-lg-4 mb-3">
          <label className="form-label">Routine *</label>
          <Select
            placeholder="Select Routine"
            value={selectedRoutine}
            onChange={(option) => {
              if (option) {
                setSelectedRoutine(option);
                formik.setFieldValue('routine_id', option?.value || 0);
              } else {
                setSelectedRoutine(null);
                formik.setFieldValue('routine_id', 0);
              }
            }}
            options={trainingData?.data?.map((item: any) => ({
              label: `${item.training_name} - ${item.training_code} -${item?.batch_name}- ${item?.hall_name}`,
              value: item.routine_master_id
            }))}
            isClearable
          />
          <FormikValidationError
            name="routine_id"
            errors={formik.errors}
            touched={formik.touched}
          />
        </div>

        <div className="col-lg-4 mb-3">
          <label className="form-label">Attendance Date *</label>
          <input
            type="date"
            className="form-control"
            value={selectedDate}
            onChange={(e) => {
              const date = e.target.value;
              setSelectedDate(date);
              formik.setFieldValue('requested_date', date);
            }}
          />
        </div>
      </div>

      <div className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5>Select Users ({selectedUsers.length} selected)</h5>
          
          {enrolledUsers.length > 0 && (
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="select-all"
                checked={isAllSelected}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
              <label className="form-check-label fw-bold" htmlFor="select-all">
                Select All
              </label>
            </div>
          )}
        </div>

        {isFetching ? (
          <p>Loading enrolled users...</p>
        ) : enrolledUsers.length > 0 ? (
          <div className="row">
            {enrolledUsers.map((user: any) => (
              <div key={user.id} className="col-lg-4 col-md-6 mb-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`user-${user.user_id}`}
                    checked={selectedUsers.includes(user?.user_id)}
                    onChange={(e) => handleUserToggle(user.user_id, e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor={`user-${user.user_id}`}>
                    {user.first_name} {user.last_name}{' '}
                    <small className="text-muted">({user.mobile_number})</small>
                  </label>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted">
            {selectedRoutine && selectedDate
              ? 'No users enrolled in this routine on selected date.'
              : 'Select routine and date to load users.'}
          </p>
        )}
      </div>
    </div>
  );
};

export default AttendanceForm;